import React, { useEffect, useState, useCallback } from "react";
import { Reorder } from "framer-motion";
import { GripVertical, Eye, EyeOff, Save, Loader2, LayoutList, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import ImageEditor from "../../components/ImageEditor";

const PAGES = [
  { key: "home", label: "Homepage", path: "/" },
  { key: "about", label: "About", path: "/about" },
  { key: "community", label: "Community", path: "/community" },
  { key: "events", label: "Events", path: "/events" },
];

const IMG_LABELS = { hero: "Hero image", founder: "Founder image" };

export default function AdminPages() {
  const [page, setPage] = useState("home");
  const [sections, setSections] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingImg, setSavingImg] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/page-config/${page}`)
      .then((r) => { setSections(r.data.sections || []); setImages(r.data.images || {}); })
      .catch(() => toast.error("Could not load page config"))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSections((prev) => prev.map((s) => s.id === id ? { ...s, enabled: !s.enabled } : s));

  const saveLayout = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/page-layout/${page}`, { sections: sections.map((s) => ({ id: s.id, enabled: s.enabled })) });
      toast.success("Layout saved");
    } catch (e) { toast.error("Could not save layout"); }
    finally { setSaving(false); }
  };

  const saveImages = async () => {
    setSavingImg(true);
    try {
      await api.put(`/admin/page-images/${page}`, { images });
      toast.success("Section images saved");
    } catch (e) { toast.error("Could not save images"); }
    finally { setSavingImg(false); }
  };

  const current = PAGES.find((p) => p.key === page);
  const imageKeys = Object.keys(images);

  return (
    <div data-testid="admin-pages">
      <h1 className="font-display text-4xl font-semibold text-primary mb-2">Page Builder</h1>
      <p className="text-muted-foreground mb-6 flex items-center gap-2">
        <LayoutList size={16} /> Reorder sections, show or hide them, and position the images on each page.
      </p>

      <div className="flex flex-wrap gap-2 mb-8">
        {PAGES.map((p) => (
          <button key={p.key} onClick={() => setPage(p.key)} data-testid={`page-tab-${p.key}`}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${page === p.key ? "bg-primary text-primary-foreground" : "border border-primary/20 text-primary hover:bg-primary/5"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap max-w-2xl">
            <h2 className="font-display text-2xl font-semibold text-primary">Sections</h2>
            <div className="flex items-center gap-3">
              <a href={current?.path} target="_blank" rel="noreferrer" className="text-secondary font-semibold inline-flex items-center gap-1 text-sm">View page <ExternalLink size={13} /></a>
              <Button onClick={saveLayout} disabled={saving} data-testid="pages-save-layout" className="rounded-full">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save layout
              </Button>
            </div>
          </div>

          <Reorder.Group axis="y" values={sections} onReorder={setSections} className="space-y-3 max-w-2xl">
            {sections.map((s) => (
              <Reorder.Item key={s.id} value={s} data-testid={`section-item-${s.id}`}
                className={`flex items-center gap-4 rounded-2xl border bg-card px-5 py-4 cursor-grab active:cursor-grabbing ${s.enabled ? "border-primary/10" : "border-dashed border-primary/20 opacity-60"}`}>
                <GripVertical size={18} className="text-muted-foreground shrink-0" />
                <span className="flex-1 font-medium text-primary">{s.label}</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{s.enabled ? "Visible" : "Hidden"}</span>
                <button data-testid={`section-toggle-${s.id}`} onClick={() => toggle(s.id)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-primary/70 hover:bg-primary/10 transition-colors">
                  {s.enabled ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
              </Reorder.Item>
            ))}
          </Reorder.Group>

          {imageKeys.length > 0 && (
            <div className="mt-12 max-w-2xl" data-testid="section-images">
              <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
                <h2 className="font-display text-2xl font-semibold text-primary">Section images</h2>
                <Button onClick={saveImages} disabled={savingImg} data-testid="section-images-save" className="rounded-full">
                  {savingImg ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Save images
                </Button>
              </div>
              <p className="text-muted-foreground mb-5">Choose each image, drag the focal point to pick what shows, then zoom or rotate.</p>
              <div className="space-y-5">
                {imageKeys.map((k) => (
                  <ImageEditor key={k} label={IMG_LABELS[k] || `${k.charAt(0).toUpperCase()}${k.slice(1)} image`}
                    value={images[k]} onChange={(v) => setImages({ ...images, [k]: v })} testid={`img-${k}`} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
